// frontend/src/pages/ShippingCrates.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

// Single shared empty form template
const EMPTY_FORM = {
  id: null,
  dim_l: "",
  dim_w: "",
  dim_h: "",
  max_kg: "",
  surcharge: "",
  ship_by: "",
  exwork: "", // Maps to the 'exwork' column (integer ID)
  active: true,
};

export default function ShippingCrates() {
  const nav = useNavigate();

  // ---------- Data ----------
  const [rows, setRows] = useState([]);
  const [originOptions, setOriginOptions] = useState([]); // List of available origins
  
  // ---------- UI State ----------
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");
  const [filterExwork, setFilterExwork] = useState(""); // Top filter (by ID)

  // ---------- Form State ----------
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [initialForm, setInitialForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [errorSave, setErrorSave] = useState("");
  const [hint, setHint] = useState("");

  // ---------- Responsive ----------
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 920
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Initial load
  useEffect(() => {
    loadOrigins();
    loadList();
  }, []);

  // Sync form origin when filter changes (convenience)
  useEffect(() => {
    if (filterExwork && !form.id) {
       setForm(prev => ({...prev, exwork: Number(filterExwork)}));
       setInitialForm(prev => ({...prev, exwork: Number(filterExwork)}));
    }
  }, [filterExwork]);

  async function loadOrigins() {
    try {
      const r = await fetch("/api/data?scope=origin");
      const j = await r.json().catch(() => ({}));
      if (j.ok && Array.isArray(j.rows)) {
        setOriginOptions(j.rows);
      }
    } catch (e) {
      console.error("Failed to load origins", e);
    }
  }

  async function loadList() {
    try {
      setLoadingList(true);
      setErrorList("");
      const r = await fetch("/api/data?scope=shipping_crates", { method: "GET" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      
      setRows(j.rows || []);
    } catch (e) {
      setErrorList(String(e.message || e));
    } finally {
      setLoadingList(false);
    }
  }

  // Filter rows based on dropdown
  const visibleRows = useMemo(() => {
    if (!filterExwork) return rows;
    const fid = Number(filterExwork);
    return rows.filter(r => Number(r.exwork) === fid);
  }, [rows, filterExwork]);

  // Helper to get name from ID
  function getOriginName(id) {
     const found = originOptions.find(o => o.opt_id === id);
     return found ? found.opt_name : (id ? `ID: ${id}` : "Unknown");
  }

  function selectRow(r) {
    const selected = {
      id: r.id,
      dim_l: r.dim_l,
      dim_w: r.dim_w,
      dim_h: r.dim_h,
      max_kg: r.max_kg,
      surcharge: r.surcharge,
      ship_by: r.ship_by || "",
      exwork: r.exwork || "", // integer
      active: r.active === true,
    };
    setForm(selected);
    setInitialForm(selected);
    setHint("Loaded ID: " + r.id);
  }

  function newRow() {
    const blank = { ...EMPTY_FORM, exwork: filterExwork ? Number(filterExwork) : "" };
    setForm(blank);
    setInitialForm(blank);
    setHint("New entry mode");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Reset changes");
  }

  const changed = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
       setForm(prev => ({ ...prev, [name]: checked }));
       return;
    }

    if (["dim_l", "dim_w", "dim_h", "max_kg", "surcharge", "exwork"].includes(name)) {
      const v = value === "" ? "" : value; // keep as string while typing
      setForm((prev) => ({ ...prev, [name]: v }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSave() {
    setErrorSave("");
    setHint("");

    if (!form.dim_l || !form.ship_by || !form.exwork) {
       setErrorSave("Length, Ship By, and Origin are required.");
       return;
    }

    const isUpdate = !!form.id;
    setSaving(true);
    
    try {
      const method = isUpdate ? "PUT" : "POST";
      const payload = { ...form };

      const r = await fetch("/api/data?scope=shipping_crates", {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);

      const savedRow = j.row;
      
      setRows((prev) => {
        if (isUpdate) {
            return prev.map(p => p.id === savedRow.id ? savedRow : p);
        } else {
            return [...prev, savedRow].sort((a,b) => {
               // Sort by exwork ID, then length
               if (a.exwork !== b.exwork) return (a.exwork || 0) - (b.exwork || 0);
               return a.dim_l - b.dim_l;
            });
        }
      });

      selectRow(savedRow);
      setHint(isUpdate ? "Saved changes" : "Created successfully");
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function back() {
    nav("/purchase/config/srp-courier");
  }

  // Layout
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
    gap: isNarrow ? 10 : 16,
    alignItems: "start",
  };

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "140px 1fr",
    gap: 10,
    alignItems: "center",
    marginBottom: 8
  };
  
  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };
  const listItemText = isNarrow ? 14 : 13;

  return (
    <div className="app-page">
      <main className="container" style={isNarrow ? { fontSize: 15 } : undefined}>
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          Shipping Crates Config
        </h2>

        {/* Top Actions */}
        <div style={{ margin: "8px 0 12px", display: "flex", flexWrap: "wrap", gap: 8, alignItems:'center' }}>
          <Button onClick={back}>← SRP Courier</Button>
          <Button onClick={newRow}>New</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset</Button>
          <Button onClick={onSave} disabled={saving || !changed}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {hint ? <span style={{ marginLeft: 4, ...smallText }}>{hint}</span> : null}
        </div>

        {/* Filter Bar */}
        <div style={{ marginBottom: 16, background: '#f5f5f5', padding: 8, borderRadius: 8 }}>
          <label style={{ fontWeight: 600, marginRight: 8 }}>Filter by Origin:</label>
          <select
            value={filterExwork}
            onChange={(e) => setFilterExwork(e.target.value)}
            style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #ccc", minWidth: 200 }}
          >
            <option value="">-- All Origins --</option>
            {originOptions.map((o) => (
              <option key={o.opt_id} value={o.opt_id}>
                {o.opt_name}
              </option>
            ))}
          </select>
        </div>

        <div style={grid}>
          {/* LEFT: list */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600 }}>
              Crates List
            </label>
            <div style={{ marginTop: 6, ...smallText }}>
               {visibleRows.length} item(s) found
            </div>

            <div
              style={{
                marginTop: 8,
                maxHeight: isNarrow ? 320 : 500,
                overflow: "auto",
                borderTop: "1px solid #eee",
              }}
            >
              {loadingList && <div style={{padding:8}}>Loading...</div>}
              
              {!loadingList && visibleRows.map((r) => {
                const sel = form.id === r.id;
                const originName = getOriginName(r.exwork);
                
                return (
                  <button
                    key={r.id}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: "8px 10px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      background: sel ? "rgba(0,0,0,0.05)" : (!r.active ? "#fff0f0" : "transparent"),
                      fontSize: listItemText,
                      opacity: r.active ? 1 : 0.6
                    }}
                  >
                    <div style={{ fontWeight: 600, display:'flex', justifyContent:'space-between' }}>
                      <span>{r.dim_l} x {r.dim_w} x {r.dim_h}</span>
                      <span style={{fontSize: 11, background:'#eee', padding:'1px 4px', borderRadius:4}}>
                        {originName}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>
                       {r.ship_by} | ${r.surcharge} | Max {r.max_kg}kg
                       {!r.active && " (Inactive)"}
                    </div>
                  </button>
                );
              })}
              
              {!loadingList && visibleRows.length === 0 && (
                <div style={{padding:16, textAlign:'center', opacity:0.6}}>No records found.</div>
              )}
            </div>
          </section>

          {/* RIGHT: editable form */}
          <section className="box" style={{ marginTop: isNarrow ? 12 : 0 }}>
            <h3 style={{marginTop:0, marginBottom: 16, borderBottom:'1px solid #eee', paddingBottom:8}}>
              {form.id ? `Edit Crate #${form.id}` : "New Crate Configuration"}
            </h3>

            <div style={fieldGrid}>
              <label>Origin / Country</label>
              <select
                className="text-input"
                name="exwork"
                value={form.exwork}
                onChange={onChange}
              >
                <option value="">-- Select Origin --</option>
                {originOptions.map((o) => (
                  <option key={o.opt_id} value={o.opt_id}>
                    {o.opt_name}
                  </option>
                ))}
              </select>

              <label>Length (mm)</label>
              <input className="text-input" type="number" name="dim_l" value={form.dim_l} onChange={onChange} />

              <label>Width (mm)</label>
              <input className="text-input" type="number" name="dim_w" value={form.dim_w} onChange={onChange} />

              <label>Height (mm)</label>
              <input className="text-input" type="number" name="dim_h" value={form.dim_h} onChange={onChange} />

              <label>Max Weight (kg)</label>
              <input className="text-input" type="number" name="max_kg" value={form.max_kg} onChange={onChange} />
              
              <label>Surcharge ($)</label>
              <input className="text-input" type="number" name="surcharge" value={form.surcharge} onChange={onChange} />
              
              <label>Ship By</label>
              <input 
                className="text-input" 
                type="text" 
                name="ship_by" 
                value={form.ship_by} 
                onChange={onChange} 
                placeholder="e.g. Courier / Sea / LTL"
              />

              <label>Active</label>
              <label style={{display:'flex', alignItems:'center'}}>
                 <input 
                   type="checkbox" 
                   name="active" 
                   checked={form.active} 
                   onChange={onChange}
                   style={{width:20, height:20}} 
                 />
                 <span style={{marginLeft:8, fontSize:12, opacity:0.7}}>
                   Enable in calculations
                 </span>
              </label>
            </div>

            {errorSave && (
              <div style={{ color: "crimson", marginTop: 10 }}>{errorSave}</div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}